'use client';

import type { ComponentProps } from 'react';
import type { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { useEffect, useRef, useState } from 'react';
import { LuPlus } from 'react-icons/lu';

export type CardCartProps = ComponentProps<typeof AddToCartButton>;
type Runtime = typeof import('./cart-button-runtime').CardCartRuntime;
let runtimePromise: Promise<Runtime> | undefined;

export function loadCardCartRuntime() {
  return (runtimePromise ??= import('./cart-button-runtime')
    .then(module => module.CardCartRuntime)
    .catch(error => {
      runtimePromise = undefined;
      throw error;
    }));
}

/** Intent warms code only. A cold click is consumed exactly once by the original cart action. */
export function DeferredCardCartButton(props: CardCartProps) {
  const { t } = useTranslations();
  const initialized = useCustomerStore(state => state.isInitialized);
  const desktop = useBreakpoint('md');
  const [Runtime, setRuntime] = useState<Runtime>();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const host = useRef<HTMLSpanElement>(null);
  const mounted = useRef(false);
  const inFlight = useRef(false);
  const pendingClick = useRef(false);
  const restoreFocus = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  function activate(click = false) {
    if (!click) {
      void loadCardCartRuntime().catch(() => {});
      return;
    }
    pendingClick.current ||= click;
    restoreFocus.current ||= !!host.current?.contains(document.activeElement);
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setFailed(false);
    loadCardCartRuntime()
      .then(component => {
        if (mounted.current) setRuntime(() => component);
      })
      .catch(() => {
        pendingClick.current = false;
        if (mounted.current) setFailed(true);
      })
      .finally(() => {
        inFlight.current = false;
        if (mounted.current) setLoading(false);
      });
  }

  useEffect(() => {
    if (!Runtime) return;
    const button = host.current?.querySelector<HTMLButtonElement>('button');
    const click = pendingClick.current;
    pendingClick.current = false;
    if (restoreFocus.current) button?.focus({ preventScroll: true });
    if (click && button && !button.disabled) button.click();
  }, [Runtime]);

  const label = props.customText ? t(props.customText) : t('text1115');
  const supplier = [...(props.product.suppliers ?? [])].sort((a, b) => a.position - b.position)[0];
  const content = (
    <>
      {(props.showIcon || !desktop) && (
        <LuPlus aria-hidden="true" size={20} className="text-white" />
      )}
      <span className="line-clamp-1 hidden text-ellipsis text-sm font-medium md:block">
        {label}
      </span>
    </>
  );
  if (supplier?.is_affiliate)
    return (
      <Button asChild className={props.className}>
        <a href={supplier.url ?? '#'} aria-label={label}>
          {content}
        </a>
      </Button>
    );
  return (
    <span ref={host} className="contents">
      {Runtime ? (
        <Runtime {...props} />
      ) : (
        <Button
          type="button"
          size={desktop ? 'default' : 'icon-scaling'}
          className={props.className}
          disabled={!initialized || !props.sellable || props.disabled || props.customValuesError}
          aria-label={label}
          aria-busy={loading}
          onPointerEnter={() => activate()}
          onFocus={() => activate()}
          onPointerDown={() => activate()}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            activate(true);
          }}
        >
          {content}
        </Button>
      )}
      {failed && (
        <span role="alert" className="sr-only">
          {t('text657')}
        </span>
      )}
    </span>
  );
}
